import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGlobalProject } from '@/contexts/GlobalProjectContext';
import { getSnapshotTrendsMetrics, getProjectSnapshots } from '@/api/analyticsApi';
import { mergeAnalytics } from '@/utils/analyticsAdapter';
import { useProjectAnalytics } from '@/hooks/useProjectAnalytics';
import type { ScheduleActivity } from '@/types';
import { ChartEmptyState } from '@/components/visuals';
import type { ChartDensity } from '@/components/visuals';
import { getChartById } from './registry';
import {
  CHART_RENDERERS,
  SNAPSHOT_REQUIRED_MESSAGE,
  SNAPSHOT_REQUIRED_ACTION_LABEL,
  snapshotRequiredActionHref,
} from './chartRenderers';
import type { ChartRenderProps } from './chartRenderers';
import type { ScheduleAnalyticsShapes } from '@/types/analyticsApi';
import type { ScheduleDateBasisValue, UnitsSpreadBasisValue } from '@/components/visuals';

// Re-export for consumers
export type { TrendsPoint } from '@/types/analyticsApi';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UseAnalyticsChartDataOptions {
  showDataLabels?: boolean;
}

/** Return type of useTrendsData (loading, hasHistory, points) */
export interface TrendsDataResult {
  loading: boolean;
  hasHistory: boolean;
  points: import('@/types/analyticsApi').TrendsPoint[];
}

/** Return type of useAnalyticsChartData — includes scheduleAnalytics and trendsData for focus/S-curve/trend charts */
export interface UseAnalyticsChartDataReturn {
  loading: boolean;
  hasData: boolean;
  merged: ReturnType<typeof mergeAnalytics> | null;
  /** Normalized schedule activities (used for time-phased units charts) */
  activities: ScheduleActivity[];
  scheduleAnalytics: Record<string, unknown> | null;
  trendsData: TrendsDataResult;
  floatDist: Array<{ bucket: string; count: number }>;
  criticalActivities: Array<{ criticalityBand: string }>;
  criticalCount: number;
  nearCriticalCount: number;
  monitorCount: number;
  allMilestones: Array<{ id: string; name: string; varianceDays: number | null }>;
  milestonesWithVariance: Array<{ id: string; name: string; varianceDays: number | null }>;
  hasBaselineData: boolean;
  wbsHotspots: Array<{ wbs_id: string; wbs_short_name: string; wbs_name?: string; hotspotScore: number }>;
  qualityChecks: Array<{ id: string; label: string; value: number; status: string }>;
  renderChart: (
    chartId: string,
    height: number | string,
    density?: ChartDensity,
    overrides?: {
      showDataLabels?: boolean;
      showBaseline?: boolean;
      maxPoints?: number;
      baselineScheduleAnalytics?: ScheduleAnalyticsShapes | null;
      scheduleDateBasis?: ScheduleDateBasisValue;
      includeMilestoneUnits?: boolean;
    }
  ) => React.ReactNode;
}

// ---------------------------------------------------------------------------
// useTrendsData
// ---------------------------------------------------------------------------

/** Hook for multi-snapshot trend charts. Returns hasHistory and points when ≥2 snapshots. */
export function useTrendsData(projectId: string | undefined, nearCriticalThresholdDays: number = 10) {
  const [loading, setLoading] = useState(false);
  const [hasHistory, setHasHistory] = useState(false);
  const [points, setPoints] = useState<import('@/types/analyticsApi').TrendsPoint[]>([]);

  useEffect(() => {
    if (!projectId) {
      setHasHistory(false);
      setPoints([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const snapshots = await getProjectSnapshots(projectId);
        if (cancelled) return;
        if (!snapshots || snapshots.length < 2) {
          setHasHistory(false);
          setPoints([]);
          return;
        }
        const data = await getSnapshotTrendsMetrics(projectId, nearCriticalThresholdDays);
        if (cancelled) return;
        setHasHistory(Boolean(data?.has_history));
        setPoints(Array.isArray(data?.points) ? data.points : []);
      } catch {
        if (!cancelled) {
          setHasHistory(false);
          setPoints([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, nearCriticalThresholdDays]);

  return { loading, hasHistory, points };
}

// ---------------------------------------------------------------------------
// useAnalyticsChartData
// ---------------------------------------------------------------------------

export function useAnalyticsChartData(options?: UseAnalyticsChartDataOptions): UseAnalyticsChartDataReturn {
  const showDataLabels = options?.showDataLabels ?? true;
  const navigate = useNavigate();
  const { selectedProject } = useGlobalProject();
  const { loading, merged, scheduleAnalytics, activities } = useProjectAnalytics();
  const trendsData = useTrendsData(selectedProject?.id, 10);

  const floatDist = useMemo(() => merged?.floatDistribution ?? [], [merged?.floatDistribution]);
  const criticalActivities = useMemo(() => merged?.criticalActivities ?? [], [merged?.criticalActivities]);
  const criticalCount = criticalActivities.filter((a) => a.criticalityBand === 'critical').length;
  const nearCriticalCount = criticalActivities.filter((a) => a.criticalityBand === 'near-critical').length;
  const monitorCount = criticalActivities.filter((a) => a.criticalityBand === 'monitor').length;
  const allMilestones = useMemo(() => merged?.milestones ?? [], [merged?.milestones]);
  const milestonesWithVariance = allMilestones.filter((m) => m.varianceDays !== null);
  const hasBaselineData = milestonesWithVariance.length > 0;
  const wbsHotspots = useMemo(() => merged?.wbsHotspots ?? [], [merged?.wbsHotspots]);
  const qualityChecks = useMemo(() => merged?.qualityChecks ?? [], [merged?.qualityChecks]);

  const renderChart = useCallback(
    (
      chartId: string,
      height: number | string,
      density: ChartDensity = 'overview',
      overrides?: {
        showDataLabels?: boolean;
        showBaseline?: boolean;
        maxPoints?: number;
        baselineScheduleAnalytics?: ScheduleAnalyticsShapes | null;
        scheduleDateBasis?: ScheduleDateBasisValue;
        includeMilestoneUnits?: boolean;
        unitsSpreadBasis?: UnitsSpreadBasisValue;
      }
    ): React.ReactNode => {
      const effectiveShowDataLabels = overrides?.showDataLabels ?? showDataLabels;
      const def = getChartById(chartId);
      if (def?.requiresSnapshotHistory && (!trendsData?.hasHistory || trendsData.points.length < 2)) {
        return (
          <ChartEmptyState
            message={SNAPSHOT_REQUIRED_MESSAGE}
            actionLabel={SNAPSHOT_REQUIRED_ACTION_LABEL}
            actionHref={snapshotRequiredActionHref(selectedProject?.id)}
          />
        );
      }
      const props: ChartRenderProps = {
        height,
        density,
        showDataLabels: effectiveShowDataLabels,
        showBaseline: overrides?.showBaseline,
        baselineScheduleAnalytics: overrides?.baselineScheduleAnalytics,
        maxPoints: overrides?.maxPoints,
        navigate: (url: string) => navigate(url),
        selectedProjectId: selectedProject?.id,
        floatDist,
        criticalActivities,
        criticalCount,
        nearCriticalCount,
        monitorCount,
        allMilestones,
        milestonesWithVariance,
        hasBaselineData,
        wbsHotspots,
        qualityChecks,
        scheduleAnalytics: (scheduleAnalytics ?? null) as ScheduleAnalyticsShapes | null,
        trendsData,
        activities,
        scheduleDateBasis: overrides?.scheduleDateBasis ?? 'baseline',
        includeMilestoneUnits: overrides?.includeMilestoneUnits ?? false,
        unitsSpreadBasis: overrides?.unitsSpreadBasis,
      };
      const renderer = CHART_RENDERERS[chartId];
      return renderer ? renderer(props) : <ChartEmptyState message="Unknown chart." />;
    },
    [
      floatDist,
      criticalActivities,
      criticalCount,
      nearCriticalCount,
      monitorCount,
      allMilestones,
      milestonesWithVariance,
      hasBaselineData,
      wbsHotspots,
      qualityChecks,
      scheduleAnalytics,
      trendsData,
      activities,
      showDataLabels,
      navigate,
      selectedProject?.id,
    ]
  );

  const result: UseAnalyticsChartDataReturn = {
    loading,
    hasData: merged !== null,
    merged,
    activities,
    scheduleAnalytics,
    trendsData,
    floatDist,
    criticalActivities,
    criticalCount,
    nearCriticalCount,
    monitorCount,
    allMilestones,
    milestonesWithVariance,
    hasBaselineData,
    wbsHotspots,
    qualityChecks,
    renderChart,
  };
  return result;
}
